import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

const VOLUME_STORAGE_KEY = 'game-audio-volume';
const DEFAULT_VOLUME = 0.2;
// Looping sounds (timer tick, war drums, sparkle, applause) are more
// fatiguing than one-shots, so we scale them down relative to master.
const LOOP_GAIN = 0.55;
const COUNTDOWN_GAIN = 0.35;

@Injectable({ providedIn: 'root' })
export class GameAudioService {
  private _isMuted = new BehaviorSubject<boolean>(false);
  isMuted$ = this._isMuted.asObservable();

  private _volume = new BehaviorSubject<number>(this.loadVolume());
  volume$ = this._volume.asObservable();

  private activeAudios = new Map<HTMLAudioElement, number>();

  private countdownAudio: HTMLAudioElement | null = null;
  private countdownSub: Subscription | null = null;

  constructor() {
    this._isMuted.subscribe(() => this.applyAllVolumes());
  }

  get isMuted(): boolean {
    return this._isMuted.getValue();
  }

  get volume(): number {
    return this._volume.getValue();
  }

  toggleMute(): void {
    this._isMuted.next(!this._isMuted.getValue());
  }

  setVolume(value: number): void {
    const clamped = Math.max(0, Math.min(1, value));
    this._volume.next(clamped);
    try {
      localStorage.setItem(VOLUME_STORAGE_KEY, String(clamped));
    } catch (e) {}
    this.applyAllVolumes();
  }

  private applyAllVolumes(): void {
    const muted = this._isMuted.getValue();
    const master = this._volume.getValue();
    this.activeAudios.forEach((gain, el) => {
      el.volume = muted ? 0 : master * gain;
    });
  }

  private loadVolume(): number {
    try {
      const raw = localStorage.getItem(VOLUME_STORAGE_KEY);
      if (raw === null) return DEFAULT_VOLUME;
      const parsed = parseFloat(raw);
      if (isNaN(parsed)) return DEFAULT_VOLUME;
      return Math.max(0, Math.min(1, parsed));
    } catch (e) {
      return DEFAULT_VOLUME;
    }
  }

  private track(audio: HTMLAudioElement, gain: number): void {
    if (!this.activeAudios.has(audio)) {
      audio.addEventListener('ended', () => this.activeAudios.delete(audio));
    }
    this.activeAudios.set(audio, gain);
    audio.volume = this._isMuted.getValue() ? 0 : this._volume.getValue() * gain;
  }

  playExisting(audio: HTMLAudioElement, gain: number = 1): void {
    if (this._isMuted.getValue()) return;
    try {
      audio.currentTime = 0;
      this.track(audio, gain);
      audio.play();
    } catch (e) {}
  }

  play(path: string, options?: { loop?: boolean; gain?: number }): HTMLAudioElement | null {
    if (this._isMuted.getValue()) return null;
    try {
      const audio = new Audio(path);
      if (options?.loop) audio.loop = true;
      // Per-call `gain` override beats the default for this kind of
      // sound (LOOP_GAIN for loops, 1 for one-shots). Use this when a
      // specific clip has been mastered low and needs a boost without
      // affecting the global LOOP_GAIN.
      const defaultGain = options?.loop ? LOOP_GAIN : 1;
      this.track(audio, options?.gain ?? defaultGain);
      audio.play();
      return audio;
    } catch (e) {
      return null;
    }
  }

  stop(audio: HTMLAudioElement | null): void {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      this.activeAudios.delete(audio);
    }
  }

  startCountdownAudio(timerValue$: Observable<number>): void {
    this.stopCountdownAudio();
    if (this._isMuted.getValue()) return;

    try {
      this.countdownAudio = new Audio('assets/game-mode/sound-effects/countDownTimer.mp3');
      this.countdownAudio.loop = true;
      this.track(this.countdownAudio, COUNTDOWN_GAIN);
      this.countdownAudio.play();
    } catch (e) {
      return;
    }

    this.countdownSub = timerValue$.subscribe(value => {
      if (!this.countdownAudio) return;
      if (value <= 0) {
        this.stopCountdownAudio();
      } else if (value <= 3) {
        this.countdownAudio.playbackRate = 1.5;
      } else {
        this.countdownAudio.playbackRate = 1.0;
      }
    });
  }

  stopCountdownAudio(): void {
    if (this.countdownSub) {
      this.countdownSub.unsubscribe();
      this.countdownSub = null;
    }
    this.stop(this.countdownAudio);
    this.countdownAudio = null;
  }
}
